import React, { useCallback, useState } from "react";
import { Drawer } from "antd";
import Button from "@dtci/spellbook/Button";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "../../hooks/usePPTToasts";
import { downloadDeliveryForms } from "ppt/api";
import { UNASSIGNED_CLIENT_KEY } from "../PPTDeliveriesPanel";
import { DeliveryNotesList } from "./DeliveryNotesList";
import styles from "./styles.module.scss";

/**
 * Drawer for a single Delivery (grouped client discounts by designator).
 * Opened from the Deliveries panel row click. Shows summary and notes
 * in a single scrollable view.
 */

const DeliveryContent = ({ delivery, promotionId, onDownload, isDownloading }) => {
  if (!delivery) return null;

  return (
    <div className={styles.drawerContent}>
      <section className={styles.section}>
        <div className={styles.summaryHeader}>
          <h4 className={styles.sectionTitle}>Delivery Summary</h4>
          <Button
            styleType="link"
            iconType="fa-download"
            onClick={onDownload}
            isFetching={isDownloading}
          >
            Download Forms
          </Button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Client</span>
            <span className={styles.summaryValue}>{delivery.label}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Total Discounts</span>
            <span className={styles.summaryValue}>{delivery.rowCount}</span>
          </div>
          {delivery.openIssueCount > 0 && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Open Issues</span>
              <span className={styles.summaryValue}>
                {delivery.openIssueCount}
              </span>
            </div>
          )}
        </div>
      </section>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Notes</h4>
        <DeliveryNotesList />
      </section>
    </div>
  );
};

export const PPTDeliveryDrawer = () => {
  const { deliveryDrawerOpen, selectedDelivery, promotionDetail } = usePPTState();
  const { closeDeliveryDrawer } = usePPTActions();
  const { showErrorToast } = usePPTToasts();
  const [isDownloading, setIsDownloading] = useState(false);

  const drawerTitle = selectedDelivery?.label || "Delivery";
  const promotionId = promotionDetail?.id;

  const handleDownload = useCallback(async () => {
    if (!selectedDelivery || !promotionId) return;

    setIsDownloading(true);
    try {
      await downloadDeliveryForms(
        promotionId,
        selectedDelivery.clientKey,
        `${selectedDelivery.label}-forms.xlsx`
      );
    } catch (error) {
      console.error("Failed to download delivery forms:", error);
      showErrorToast(`Failed to download ${selectedDelivery.label} forms. Please try again.`);
    } finally {
      setIsDownloading(false);
    }
  }, [selectedDelivery, promotionId, showErrorToast]);

  return (
    <Drawer
      className="delivery-drawer"
      title={drawerTitle}
      width={750}
      placement="right"
      open={deliveryDrawerOpen}
      onClose={closeDeliveryDrawer}
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={closeDeliveryDrawer}
          >
            Close
          </Button>
        </div>
      }
    >
      <DeliveryContent
        delivery={selectedDelivery}
        promotionId={promotionId}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </Drawer>
  );
};
